import { Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import {
  createGetNextPersonOrUndefined,
  createGetPerson,
  createGetPreviousPersonOrUndefined,
} from '../states/offer/offer.selector';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { PersonService } from './person.service';
import { PersonState } from '../states/offer/offer.state';

export interface NavigationDetails {
  routerLink: string;
  translationKey: string;
  translationParams?: Record<string, string>;
}

export type NavigationDirection = 'forwards' | 'backwards';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  constructor(
    private store: Store,
    private personService: PersonService,
  ) {}

  public getNavigationDetailsForNextPersonOrFallback$(
    currentPersonId: number,
    navigationDirection: NavigationDirection,
    routerLink: string,
    translationKey: string,
    routerLinkFallback: string,
    translationKeyFallback: string,
  ): Observable<NavigationDetails> {
    return this.getPersonForDirection$(currentPersonId, navigationDirection).pipe(
      switchMap((person) => {
        if (!person) {
          return of({
            routerLink: routerLinkFallback,
            translationKey: translationKeyFallback,
          });
        }
        return this.getNavigationDetailsForPersonState$(person, routerLink, translationKey);
      }),
    );
  }

  public getNavigationDetailsForPerson$(
    personId: number,
    routerLink: string,
    translationKey: string,
  ): Observable<NavigationDetails> {
    return this.store
      .select(createGetPerson(personId))
      .pipe(switchMap((person) => this.getNavigationDetailsForPersonState$(person, routerLink, translationKey)));
  }

  private getPersonForDirection$(
    personId: number,
    navigationDirection: NavigationDirection,
  ): Observable<PersonState | undefined> {
    if (navigationDirection === 'forwards') {
      return this.store.select(createGetNextPersonOrUndefined(personId));
    }
    return this.store.select(createGetPreviousPersonOrUndefined(personId));
  }

  private getNavigationDetailsForPersonState$(
    person: PersonState,
    routerLink: string,
    translationKey: string,
  ): Observable<NavigationDetails> {
    return this.personService.getFirstNameOrFallback$(person.details.firstName, person.personId).pipe(
      map((firstName) => {
        return {
          routerLink: routerLink + person.personId,
          translationKey: translationKey,
          translationParams: { personName: firstName },
        };
      }),
    );
  }
}
